import { useEffect, useRef } from 'react';

/**
 * O VOLTAR DO NAVEGADOR FECHA A CASCA ABERTA (14/10/2026, PR-C2 do plano das rotas, opção (a) do Heitor).
 *
 * Com a URL por tela (PR-C1), o Voltar troca de tela. Com uma casca aberta (modal ou gaveta), ele trocava a tela POR
 * BAIXO dela, e a casca continuava aberta por cima da tela errada. Agora o Voltar fecha a casca primeiro, pelo MESMO
 * caminho do Esc: leitura fecha; formulário sem nada digitado fecha; com algo digitado PERGUNTA ("Fechar sem salvar?");
 * operação em curso (não `fechavel`) engole.
 *
 * Como: ao abrir, a casca empilha no histórico uma entrada com a MESMA URL e a marca dela no `state`. O Voltar consome
 * essa entrada (a URL não muda, então a tela fica) e o `popstate` chega à casca de CIMA, que age.
 *  - Ela re-empilha a marca ANTES de agir: se a pessoa cancela o "Fechar sem salvar?", a casca segue aberta e o próximo
 *    Voltar volta a perguntar; se fecha, a desmontagem tira a marca.
 *  - Fechar pelo × / Cancelar / Salvar (desmontagem normal) tira a própria marca com um `history.back()` que nenhuma
 *    casca trata (`ignorar`), para o histórico não acumular entradas mortas.
 *  - Se a casca fecha porque a pessoa NAVEGOU (uma ação de dentro dela abriu outra tela), a marca já não está no topo:
 *    não volta, senão desfaria a navegação.
 *  - Cascas aninhadas: só a de cima reage (a pilha é a mesma ideia da do Esc).
 *  - StrictMode (dev) monta, desmonta e monta de novo: a marca é por instância (ref), a desmontagem falsa é adiada um
 *    tique e cancelada pela remontagem, e a remontagem não empilha de novo.
 *
 * O ENDEREÇO DA CASCA (15/10/2026, estado na URL). Com `endereco`, a entrada empilhada leva essa URL em vez da mesma:
 * "/clientes?cliente=5" enquanto a ficha está aberta. Copiar o link ou dar F5 reabre a ficha; o Voltar e o fechar tiram
 * a entrada, e a URL volta a ser a da tela, sem tocar na entrada de baixo. É lido só na montagem.
 */
const pilha: symbol[] = [];
const acoes = new Map<symbol, () => void>();
let ignorar = 0;
let serial = 0;
/** Marcas de cascas que já fecharam de verdade. A faxina tira do topo do histórico as que ficaram para trás. */
const mortas = new Set<string>();
let faxinando = false;

/**
 * A FAXINA EM CASCATA (14/10/2026, achada na conferência em localhost). Duas cascas que fecham juntas (o "Fechar sem
 * salvar" fecha a pergunta E a ficha) deixavam a marca de baixo para trás: quando o tique da ficha rodava, o topo ainda
 * era a marca da pergunta (o back dela é assíncrono), e a ficha não voltava. Sobrava uma entrada morta, e o Voltar
 * seguinte não fazia nada visível. Agora toda casca que fecha só marca a dela como MORTA, e a faxina tira uma morta do
 * topo por vez: cada `popstate` da própria faxina chama a faxina de novo, até o topo ser uma marca viva ou a tela.
 */
function faxina() {
  if (faxinando || typeof window === 'undefined') return;
  const topo = marcaNoTopo();
  if (!topo || !mortas.has(topo)) return;
  faxinando = true;
  ignorar++;
  window.history.back();
}

/**
 * UM ouvinte de `popstate` para o módulo inteiro, instalado uma vez. ⛔ Não pode ser um por casca: o `history.back()`
 * que tira a marca de uma casca fechada dispara o `popstate` quando ela já tirou o próprio ouvinte; sem ninguém para
 * consumir o `ignorar`, o próximo Voltar de verdade era engolido (medido no teste: fechar uma casca e voltar na
 * seguinte não fazia nada).
 */
let instalado = false;
function instalar() {
  if (instalado || typeof window === 'undefined') return;
  instalado = true;
  window.addEventListener('popstate', () => {
    if (ignorar > 0) { ignorar--; faxinando = false; faxina(); return; }
    const topo = pilha[pilha.length - 1];
    const acao = topo ? acoes.get(topo) : undefined;
    if (acao) acao();
  });
}

const marcaNoTopo = () => (window.history.state && typeof window.history.state === 'object' ? window.history.state.hubCasca : undefined);

export function useVoltarFecha(aoVoltar: (() => void) | null, endereco?: string) {
  const acaoRef = useRef(aoVoltar);
  acaoRef.current = aoVoltar;
  const idRef = useRef<string>('');
  if (!idRef.current) idRef.current = `casca-${++serial}`;
  const montado = useRef(false);
  const enderecoRef = useRef(endereco);

  useEffect(() => {
    instalar();
    const id = idRef.current;
    const eu = Symbol(id);
    montado.current = true;
    pilha.push(eu);
    if (marcaNoTopo() !== id) window.history.pushState({ ...(window.history.state || {}), hubCasca: id }, '', enderecoRef.current);

    acoes.set(eu, () => {
      // A entrada da casca foi consumida pelo Voltar: re-empilha antes de agir (ver acima).
      window.history.pushState({ ...(window.history.state || {}), hubCasca: id }, '', enderecoRef.current);
      const acao = acaoRef.current;
      if (acao) acao();
    });
    return () => {
      acoes.delete(eu);
      const i = pilha.indexOf(eu);
      if (i >= 0) pilha.splice(i, 1);
      montado.current = false;
      setTimeout(() => {
        if (montado.current) return; // remontagem do StrictMode: a casca segue aberta
        mortas.add(id);
        faxina();
      }, 0);
    };
  }, []);
}
